import { QueryClient, useQuery } from '@tanstack/react-query';
import { notification } from 'antd';

import { SortType } from '@/entities/filters';
import { apiV1 } from '@/lib/axios';
import { ExtractFnReturnType, QueryConfig } from '@/lib/react-query';

import { Company } from '@company/entities/company';
import { companiesSchema } from '@company/schemas';

export type CompanySortKey = keyof Omit<Company, 'id' | 'createdAt'>;

export type CompanyFilters = {
  status?: string;
  responsibles?: string;
  tags?: string;
  search?: string;
  sort?: SortType<CompanySortKey>;
  page?: string;
  limit?: string;
  myCompany?: `0` | `1`;
};

export const getCompanies = async (filters: CompanyFilters) => {
  const response = await apiV1.get(`/v1/companies`, {
    params: {
      ...filters,
    },
  });

  return companiesSchema.parse(response?.data);
};

type QueryFnType = typeof getCompanies;

type UseCompaniesOptions = {
  filters?: CompanyFilters;
  config?: QueryConfig<QueryFnType>;
};

export const companiesQuery = ({ filters, config }: UseCompaniesOptions) => ({
  queryKey: [`companies`, filters],
  queryFn: () => getCompanies({ ...filters }),
  onError: () => {
    notification.error({ message: `Não foi possível buscar todas as empresas` });
  },
  ...config,
});

export const useCompanies = (options: UseCompaniesOptions) => {
  return useQuery<ExtractFnReturnType<QueryFnType>>(companiesQuery(options));
};

type companiesLoaderOptions = {
  params: CompanyFilters;
};

export const companiesLoader =
  (queryClient: QueryClient) =>
  async ({ params }: companiesLoaderOptions) => {
    const query = companiesQuery({ filters: { page: `1`, limit: `20`, ...params } });
    return queryClient.getQueryData(query.queryKey) ?? queryClient.fetchQuery(query);
  };
